<template>
  <div class="calendar-item">
    <!-- Action buttons -->
    <div v-if="props.isActionButtonsVisible" class="action-buttons calendar-item__action-buttons">
      <div class="action-buttons__left-side"></div>

      <div class="action-buttons__right-side">
        <CircleWithIconBtn class="action-buttons__button" iconName="pencil_2" color="white" :iconSize="18" size="huge" @click="emit('edit')" />

        <CircleWithIconBtn class="action-buttons__button" iconName="trashcan-alt_2" color="white" :iconSize="18" size="huge" @click="emit('delete')" />
      </div>
    </div>
    <!-- / Action buttons -->

    <div v-if="[CalendarItemGenStatus.Pending, CalendarItemGenStatus.Generating].includes(props.calendarItem.genStatus)" class="calendar-item__status-wrap">
      <CollabStatusStripe class="calendar-item__status" :text="props.calendarItem.genStatus" :color="props.calendarItem.genStatus === CalendarItemGenStatus.Generating ? 'green' : 'white'">
        <template #icon>
          <LoadingIcon :size="12" />
        </template>
      </CollabStatusStripe>
    </div>

    <h2 class="global-h2 calendar-item__title">
      {{ props.calendarItem.title }}
    </h2>

    <div class="calendar-item__description">
      {{ props.calendarItem.description }}
    </div>

    <div v-if="props.calendarItem.specificDate" class="calendar-item__date"><IconEmbedded name="calendar_2" :size="19" color="rgba(0, 0, 0, 0.4)" /> {{ props.calendarItem.specificDate }}</div>

    <CalendarItemGenDebug v-if="props.showDebug && props.calendarItem.genMetaInfo" :genMetaInfo="props.calendarItem.genMetaInfo" />
  </div>
</template>

<script setup lang="ts">
// Types
import { ContentCalendarItem, CalendarItemGenStatus } from "@contracts/contentCalendar";

// Components
import CircleWithIconBtn from "@components/ui/CircleWithIconBtn.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import CollabStatusStripe from "@components/CollabStatusStripe.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import CalendarItemGenDebug from "@components/CalendarItemGenDebug.vue";

const props = withDefaults(
  defineProps<{
    calendarItem: ContentCalendarItem;
    isActionButtonsVisible?: boolean;
    showDebug?: boolean;
  }>(),
  {
    isActionButtonsVisible: false,
    showDebug: false,
  }
);

const emit = defineEmits<{
  (e: "edit"): void;
  (e: "delete"): void;
}>();
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";

// Action buttons =============================================================
.action-buttons {
  display: flex;
  justify-content: space-between;
  align-items: center;

  // left side ------------------------
  &__left-side {
    gap: 6px;
    display: flex;
    align-items: center;
  }

  // right side -----------------------
  &__right-side {
    gap: 6px;
    display: flex;
    align-items: center;
  }

  &__button {
    &--drag-and-drop-handle {
      content: "";
    }

    &--invisible {
      opacity: 0;
    }
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .action-buttons {
    // right side -----------------------
    &__right-side {
      gap: 2px;
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
}

// Calendar item ==============================================================
.calendar-item {
  @include standardFont;
  padding: 18px 23px 22px;
  box-sizing: border-box;
  border-radius: 8px;
  outline: 1px rgba(215, 215, 215, 1) solid;
  outline-offset: -1px;
  position: relative;
  z-index: 0;
  background: rgba(255, 255, 255, 1);
  box-shadow: 0 7px 32px -10px rgba(0, 0, 0, 0.4);

  &--darkened {
    box-shadow: none;

    &::before {
      content: "";
      width: 100%;
      height: 100%;
      border-radius: 8px;
      position: absolute;
      inset: 0 auto auto 0;
      z-index: 2;
      background: rgba(230, 230, 230, 1);
    }
  }

  &__action-buttons {
    width: 100%;
    position: absolute;
    z-index: 3;
    inset: -17px auto auto 0;
  }

  &__tag {
    padding: 0 8px 2px;
    border: 1px rgba(0, 0, 0, 0.2) solid;
    border-radius: 100px;
    display: inline-flex;
    position: absolute;
    inset: -10px auto auto 20px;
    font-weight: bold;
    background: rgba(255, 255, 255, 1);
  }

  &__status-wrap {
    padding-bottom: 13px;
    margin: 6px 0 11px;
    border-bottom: 1px rgba(0, 0, 0, 0.15) solid;
    display: flex;
  }

  &__status {
  }

  &__title {
    margin-bottom: 6px;
  }

  &__description {
    margin-bottom: 11px;
    white-space: pre-wrap;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__date {
    gap: 4px;
    padding-top: 8px;
    border-top: 1px rgba(0, 0, 0, 0.15) solid;
    display: flex;
    align-items: center;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
}
</style>
